/**
 * Bundle Cart Behavior
 * - Intercepts ATC form submit (capture phase) to prevent cart page navigation
 * - Adds to cart via fetch, shows "Added!", updates cart badge
 * - Hides "You may also like", shows post-ATC section, scrolls to it
 * - Dispatches cart:updated + bundle:cart-updated for bundle components
 */
(function() {
  'use strict';

  // --- ATC form interception (capture phase — runs before theme handler) ---
  document.addEventListener('submit', function(e) {
    var form = e.target;
    // Only intercept product add-to-cart forms
    if (!form || (form.getAttribute('is') !== 'product-form' && !(form.action || '').includes('/cart/add'))) return;
    // Only on product pages where post-ATC section exists
    if (!document.getElementById('bundle-post-atc')) return;

    e.preventDefault();
    e.stopPropagation();

    var btn = form.querySelector('.product-form__submit') || form.querySelector('[name="add"]');
    var textSpan = btn && btn.querySelector('.btn-text > span');
    var originalText = textSpan ? textSpan.textContent : '';

    // Show working state
    if (btn) {
      btn.setAttribute('aria-busy', 'true');
      btn.setAttribute('aria-disabled', 'true');
    }

    // Build form data — enable the hidden id input first
    var hiddenId = form.querySelector('input[name="id"][disabled]');
    if (hiddenId) hiddenId.disabled = false;
    var formData = new FormData(form);
    if (hiddenId) hiddenId.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
    .then(function(res) {
      if (!res.ok) throw new Error('ATC failed: ' + res.status);
      return res.json();
    })
    .then(function(item) {
      // Show "Added!" on button
      if (textSpan) {
        textSpan.textContent = '\u2713 Added!';
        setTimeout(function() {
          textSpan.textContent = originalText;
        }, 1500);
      }
      if (btn) {
        btn.removeAttribute('aria-busy');
        btn.removeAttribute('aria-disabled');
      }

      // Show confirmation banner in post-ATC stepper
      var confirmBanner = document.querySelector('.patc-confirm-banner');
      var confirmText = document.querySelector('.patc-confirm-text');
      if (confirmBanner && confirmText) {
        var productTitle = item.product_title || 'Item';
        confirmText.textContent = productTitle + ' added to cart';
        confirmBanner.style.display = 'flex';
      }

      // Update cart badge
      updateCartBadge();

      // Hide "You may also like", show post-ATC section
      toggleSections(true);

      // Scroll to post-ATC section — offset for sticky header + breathing room
      var postAtc = document.getElementById('bundle-post-atc');
      if (postAtc) {
        requestAnimationFrame(function() {
          var header = document.querySelector('.shopify-section-header') || document.querySelector('header');
          var headerH = header ? header.getBoundingClientRect().height : 0;
          var targetY = postAtc.getBoundingClientRect().top + window.pageYOffset - headerH - 20;
          window.scrollTo({ top: targetY, behavior: 'smooth' });
        });
      }

      // Dispatch events for other bundle components to refresh
      document.dispatchEvent(new CustomEvent('cart:updated'));
      document.dispatchEvent(new CustomEvent('bundle:cart-updated'));
      // Also fire the theme's event so other theme components update
      document.dispatchEvent(new CustomEvent('ajaxProduct:added'));

      // Refresh the cart drawer's section HTML directly.
      // The theme's cart-items uses pubsub (not our DOM events) with section bundling.
      // We bypass that and re-fetch the section ourselves, replacing MiniCart innerHTML.
      refreshCartDrawer();

      // Force all steppers to re-render after section is visible
      // Belt-and-suspenders: the custom element's deferred connectedCallback
      // handles initial sync, but this ensures re-render after ATC
      setTimeout(function() {
        document.querySelectorAll('bundle-tier-display').forEach(function(el) {
          if (typeof el.load === 'function') el.load();
        });
        // Override post-ATC nudge text after stepper renders
        // The stepper's render() sets tier-based nudge text; we override
        // the post-ATC instance only to show discovery messaging
        overridePostAtcNudge();
      }, 100);
    })
    .catch(function(err) {
      console.error('[ATC] Failed:', err);
      if (textSpan) textSpan.textContent = originalText;
      if (btn) {
        btn.removeAttribute('aria-busy');
        btn.removeAttribute('aria-disabled');
      }
      // Fallback: show error briefly on button
      if (textSpan) {
        textSpan.textContent = 'Error \u2014 try again';
        setTimeout(function() { textSpan.textContent = originalText; }, 2000);
      }
    });
  }, true); // capture phase — fires before theme's handler

  // --- Cart drawer refresh via Section Rendering API ---
  function refreshCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    var sectionId = drawer.getAttribute('data-section-id');
    if (!sectionId) return;
    var miniCartId = 'MiniCart-' + sectionId;
    var miniCart = document.getElementById(miniCartId);
    if (!miniCart) return;

    var rootUrl = (window.theme && theme.routes && theme.routes.root_url) || '/';
    fetch(rootUrl + '?section_id=' + sectionId)
      .then(function(r) { return r.text(); })
      .then(function(html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newContent = doc.getElementById(miniCartId);
        if (newContent) {
          miniCart.innerHTML = newContent.innerHTML;
        }
      })
      .catch(function() {});
  }

  // --- Cart badge updater ---
  function updateCartBadge() {
    fetch('/cart.js')
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        // Update all cart count elements (header, drawer, mobile dock)
        document.querySelectorAll('.header__cart-count, [data-cart-count], cart-count, .cart-count-bubble span[aria-hidden]').forEach(function(el) {
          el.textContent = cart.item_count;
          // Remove hidden attribute on 0→1 transition
          if (cart.item_count > 0) el.removeAttribute('hidden');
          else el.setAttribute('hidden', '');
        });
        var bubble = document.querySelector('.cart-count-bubble');
        if (bubble) {
          var span = bubble.querySelector('span[aria-hidden="true"]');
          if (span) span.textContent = cart.item_count;
          bubble.classList.toggle('hidden', cart.item_count === 0);
        }
      })
      .catch(function() {});
  }

  // --- Section show/hide ---
  function toggleSections(showPostATC) {
    // Hide "You may also like" / product-recommendations
    document.querySelectorAll('h2').forEach(function(h2) {
      var text = h2.textContent.trim().toLowerCase();
      if (text.includes('you may also like') || text.includes('recently viewed')) {
        var section = h2.closest('.shopify-section') || h2.closest('section');
        if (section) section.style.display = showPostATC ? 'none' : '';
      }
    });
    // Also hide the product-recommendations section by ID
    var recsSection = document.querySelector('.shopify-section-product-recommendations');
    if (recsSection) recsSection.style.display = showPostATC ? 'none' : '';

    // Show/hide post-ATC
    var postAtc = document.getElementById('bundle-post-atc');
    if (postAtc) {
      postAtc.style.display = showPostATC ? 'block' : 'none';
    }
  }

  // --- Post-ATC nudge text override ---
  // After ATC, replace the tier-based nudge with discovery messaging
  // in the post-ATC stepper only. Uses MutationObserver with re-entry
  // guard to keep the override even if the stepper re-renders.
  var patcNudgeGuard = false;
  function overridePostAtcNudge() {
    var postAtc = document.getElementById('bundle-post-atc');
    if (!postAtc) return;
    var nudge = postAtc.querySelector('[data-nudge-text]');
    if (!nudge) return;
    patcNudgeGuard = true;
    nudge.textContent = 'Other items you may love';
    nudge.style.color = '#1a7a4c';
    patcNudgeGuard = false;

    // Watch for the stepper's render() resetting the text
    if (!nudge._patcObserver) {
      var obs = new MutationObserver(function() {
        if (patcNudgeGuard) return;
        if (nudge.textContent !== 'Other items you may love') {
          patcNudgeGuard = true;
          nudge.textContent = 'Other items you may love';
          nudge.style.color = '#1a7a4c';
          patcNudgeGuard = false;
        }
      });
      obs.observe(nudge, { childList: true, characterData: true, subtree: true });
      nudge._patcObserver = obs;
    }
  }

  // --- Initial state: hide post-ATC section on page load ---
  function initSections() {
    var postAtc = document.getElementById('bundle-post-atc');
    if (postAtc) postAtc.style.display = 'none';
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSections);
  } else {
    initSections();
  }

  // --- CSS: suppress the btn-fill sliding animation, keep text visible ---
  var s = document.createElement('style');
  s.textContent =
    '.product-form__submit[aria-busy="true"] .btn-fill,' +
    '.product-form__submit[aria-busy="true"]::after,' +
    '.product-form__submit[aria-busy="true"]::before{' +
    'display:none!important;opacity:0!important}' +
    '.product-form__submit[aria-busy="true"]{opacity:.7;cursor:wait}' +
    '.product-form__submit[aria-busy="true"] .btn-text{' +
    'opacity:1!important;visibility:visible!important}';
  document.head.appendChild(s);
})();